/**
 * SMS — 路由表（AppRoutes）＋ 角色入口閘（P1.5-A 重整；`decisions/015`）
 *
 * 兩種路由並存（⚠️ 舊嘅一律保留 —— 舊書籤／WhatsApp 深連結唔可以死）：
 *   · **新（tab 入口）**：`/home`（總覽）｜`/me`（我嘅資料）｜`/orders`（供應商訂單）｜
 *     `/purchase`（採購）｜`/stock`（庫存公式版）｜`/incoming`（物流到貨）｜`/pickup`（物流出貨指示）｜
 *     `/fees`（物流倉租）｜`/disposition`（貨去向）｜`/password`（改密碼）
 *   · **舊**：`/intakes*`｜`/inventory`｜`/movements`｜`/outbound*`｜`/weekly-fees`｜`/reports`｜
 *     `/pending`｜`/master`｜`/settings`｜`/o/:orderNo`
 *
 * ⚠️ 前端 `Navigate` **唔係**權限：後端每個 route 自己 `guard()` 再擋一次。
 * ⚠️ `/` 會按角色彈去第一個 tab（供應商 → `/me`、物流 → `/incoming`、職員／admin → `/home`）。
 */
import { Navigate, Route, Routes } from 'react-router-dom';
import ChangePassword from '../pages/ChangePassword';
import Dashboard from '../pages/Dashboard';
import IntakeDetail from '../pages/IntakeDetail';
import IntakeNew from '../pages/IntakeNew';
import Intakes from '../pages/Intakes';
import Inventory from '../pages/Inventory';
import Master from '../pages/Master';
import MeProfile from '../pages/MeProfile';
import Movements from '../pages/Movements';
import OrderLink from '../pages/OrderLink';
import Orders from '../pages/Orders';
import Outbound from '../pages/Outbound';
import OutboundDetail from '../pages/OutboundDetail';
import OutboundNew from '../pages/OutboundNew';
import Pending from '../pages/Pending';
import Picklist from '../pages/Picklist';
import Purchase from '../pages/Purchase';
import Reconcile from '../pages/Reconcile';
import Reports from '../pages/Reports';
import Settings from '../pages/Settings';
import WeeklyFees from '../pages/WeeklyFees';
import { tabsFor } from '../lib/role-tabs';
import type { Me } from '../lib/api';

/** 角色首頁：`/` → 該角色第一個 tab */
function RoleHome({ role }: { role: Me['role'] }) {
  return <Navigate to={tabsFor(role)[0].to} replace />;
}

export default function AppRoutes({ user }: { user: Me }) {
  const isAdmin = user.role === 'admin';
  const isStaff = user.role === 'staff';
  const isInternal = isStaff || isAdmin;
  const isSupplier = user.role === 'supplier';
  const isLogistics = user.role === 'logistics';
  /** 物流只做收貨對數（開單由供應商／職員做）→ 後端都會擋，前端先唔顯示入口 */
  const canCreateIntake = user.role !== 'logistics';
  const home = <RoleHome role={user.role} />;

  return (
    <Routes>
      {/* 首頁 → 按角色彈去第一個 tab */}
      <Route path="/" element={<RoleHome role={user.role} />} />

      {/* 總覽（內部）—— 原本 `/` 嘅儀表板搬嚟呢度 */}
      <Route path="/home" element={isInternal ? <Dashboard /> : home} />

      {/* 我嘅資料（供應商；admin 都睇得） */}
      <Route path="/me" element={isSupplier || isAdmin ? <MeProfile user={user} /> : home} />

      {/* 訂單（供應商收 CoEldery 85 採購單；P1.5-B）、採購（職員落單；P1.5-B） */}
      <Route path="/orders" element={isSupplier ? <Orders /> : home} />
      <Route path="/purchase" element={isInternal ? <Purchase /> : home} />

      {/* 庫存（三個角色共用；公式版：入 − 出 ＝ 現時） */}
      <Route path="/stock" element={<Inventory user={user} />} />

      {/* 物流：到貨／出貨指示／倉租 */}
      <Route path="/incoming" element={isLogistics || isInternal ? <Intakes user={user} /> : home} />
      <Route path="/pickup" element={isLogistics || isInternal ? <Outbound user={user} /> : home} />
      <Route path="/fees" element={isLogistics || isInternal ? <WeeklyFees user={user} /> : home} />

      {/* 貨去向（＝未完事項；內部＋物流） */}
      <Route path="/disposition" element={isInternal || isLogistics ? <Pending /> : home} />

      {/* 改密碼（全部角色） */}
      <Route path="/password" element={<ChangePassword />} />

      {/* ── 舊路由（保留；唔准 404）───────────────────────────────── */}
      <Route path="/intakes" element={<Intakes user={user} />} />
      <Route
        path="/intakes/new"
        element={canCreateIntake ? <IntakeNew user={user} /> : <Navigate to="/intakes" replace />}
      />
      <Route path="/intakes/:id" element={<IntakeDetail user={user} />} />
      <Route path="/inventory" element={<Inventory user={user} />} />
      <Route path="/movements" element={<Movements user={user} />} />
      <Route path="/outbound" element={isSupplier ? home : <Outbound user={user} />} />
      <Route path="/outbound/new" element={isInternal ? <OutboundNew /> : <Navigate to="/outbound" replace />} />
      <Route path="/outbound/:id" element={isSupplier ? home : <OutboundDetail user={user} />} />
      <Route path="/outbound/:id/picklist" element={isSupplier ? home : <Picklist />} />
      <Route path="/outbound/:id/reconcile" element={isSupplier ? home : <Reconcile user={user} />} />
      <Route path="/weekly-fees" element={isInternal || isLogistics ? <WeeklyFees user={user} /> : home} />
      <Route path="/reports" element={isInternal ? <Reports /> : home} />
      <Route path="/pending" element={isInternal || isLogistics ? <Pending /> : home} />
      <Route path="/o/:orderNo" element={isSupplier ? home : <OrderLink user={user} />} />
      <Route path="/master" element={isAdmin ? <Master /> : home} />
      <Route path="/settings" element={isAdmin ? <Settings /> : home} />
      <Route path="*" element={home} />
    </Routes>
  );
}
